import { faSearchPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormControlState, Grid } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import useOutsideClick from '../../../../../../../hooks/useOutsideClick'
import { useResultWithLeftEffect } from '../../../../../../../hooks/useResultWithLeftEffect'
import {
  latestFeaturedProductsAutoComplete$,
  Product,
  searchFeaturedProductsAutoComplete,
  useFeaturedProductsAutoComplete,
} from '../../../../../../../lib/sanity-dispensaries/manage/featured-products'
import { constUndefined } from '../../../../../../../utils/constant'
import { ProductListSearchResult } from '../ProductListSearchResult'
import * as Styled from './styled'

export interface ProductListSearchProps {
  onSelect: (product: Product) => void
}

const pluckData = <T,>({ data }: { data: T }) => data

const ProductListSearchContent: React.FC<ProductListSearchProps> = ({ onSelect }) => {
  const [resultsLeft, results] = useResultWithLeftEffect(
    useFeaturedProductsAutoComplete(),
    pluckData,
    constUndefined,
  )

  const [searchInput, setSearchInput] = useState('')
  const [showResults, setShowResults] = useState<boolean>(false)

  const openResults = useCallback(() => setShowResults(true), [])

  const closeResults = useCallback(() => setShowResults(false), [])

  const { elementRef: outsideClickRef } = useOutsideClick<HTMLDivElement>(closeResults)

  const closeOnEscape = useCallback(
    (event: { key: string }) => {
      if (event.key === 'Escape') {
        closeResults()
      }
    },
    [closeResults],
  )

  const showInvalidSearch =
    showResults &&
    !!searchInput &&
    (resultsLeft || (results && results.products.length === 0 && !results.loading))

  const showSearchResults =
    showResults && !!searchInput && !resultsLeft && results && results.products.length > 0

  const inputRef = useRef<HTMLInputElement & FormControlState>(null)
  const focusInput = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  const onResultClick = useCallback(
    (result: Product) => {
      closeResults()
      onSelect(result)
    },
    [closeResults, onSelect],
  )

  useEffect(() => {
    if (searchInput) {
      searchFeaturedProductsAutoComplete({ query: searchInput })
    }
  }, [searchInput])

  const shrink = !!searchInput || (!!inputRef.current && inputRef.current.focused)

  const [isNarrowScreen, setIsNarrowScreen] = useState<boolean>(window.innerWidth < 540)

  useEffect(() => {
    const handleResize = () => setIsNarrowScreen(window.innerWidth < 540)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <div onKeyUp={closeOnEscape} onFocus={openResults} ref={outsideClickRef}>
      <Styled.SearchInputContainerGrid
        container
        justifyContent="space-between"
        alignItems="center"
        onClick={focusInput}
      >
        <Styled.IconContainerGrid item xs={isNarrowScreen ? 2 : 1} onClick={focusInput}>
          <FontAwesomeIcon className="icon" icon={faSearchPlus} onClick={focusInput} />
        </Styled.IconContainerGrid>
        <Grid item xs={isNarrowScreen ? 9 : 10}>
          <Styled.SearchInput
            name="search"
            label="Search DFH or enter a SKU"
            inputRef={inputRef}
            autoComplete="off"
            onChange={(event: { target: { value: React.SetStateAction<string> } }) =>
              setSearchInput(event.target.value)
            }
            InputLabelProps={{ shrink }}
            InputProps={{ disableUnderline: true }}
            variant="filled"
          />
        </Grid>
        <Grid item xs={isNarrowScreen ? 1 : 1} onClick={focusInput}>
          {results?.loading && <Styled.SearchLoadIcon loading size={16} />}
        </Grid>
      </Styled.SearchInputContainerGrid>
      <Styled.AutoCompleteContainer>
        {showSearchResults && (
          <Styled.SuggestItemList>
            {results?.products.map((result) => (
              <Styled.SuggestItemListEntry key={result.id} onClick={() => onResultClick(result)}>
                <ProductListSearchResult result={result} displayMode="compact" noNavigate />
              </Styled.SuggestItemListEntry>
            ))}
          </Styled.SuggestItemList>
        )}
        {showInvalidSearch && (
          <Styled.NoSearchResultsContainer>
            <p>
              Your search - <Styled.NoSearchResultsSpan>{searchInput}</Styled.NoSearchResultsSpan> -
              did not return any results.
            </p>
            <p>Suggestions:</p>
            <Styled.NoSearchResultsList>
              <li>Make sure all words are spelled correctly.</li>
              <li>Try different or fewer keywords.</li>
              <li>If searching a SKU, try the first few characters.</li>
            </Styled.NoSearchResultsList>
          </Styled.NoSearchResultsContainer>
        )}
      </Styled.AutoCompleteContainer>
    </div>
  )
}

export const ProductListSearch: React.FC<ProductListSearchProps> = ({ onSelect }) => {
  return (
    <Subscribe source$={latestFeaturedProductsAutoComplete$}>
      <ProductListSearchContent onSelect={onSelect} />
    </Subscribe>
  )
}
